import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, filter } from 'rxjs';
import { environment } from '../../environments/environment';
import { TrackClickDirective } from '../directives/track-click.directive';
import { PageKey, routerCommands, toLang } from '../seo/seo.config';
import { FeaturesService } from '../services/features.service';
import { LanguageService } from '../services/language.service';
import { SeoService } from '../services/seo.service';
import { IconComponent } from './icon.component';

@Component({
  selector: 'app-navbar',
  imports: [RouterLink, RouterLinkActive, TranslateModule, IconComponent, TrackClickDirective],
  template: `
    <header class="nav" data-screen-label="Nav">
      <div class="wrap nav-in">
        <a [routerLink]="link('home')" [attr.aria-label]="'nav.home' | translate"><img src="/assets/logo-color-240.webp" class="logo" alt="Chattoo" width="240" height="64" /></a>
        <!-- Canali · Funzioni · Come funziona · Prezzi · Casi d'uso · Blog.
             Chi siamo e Contatti sono scesi nel piè di pagina per non affollare il menu. -->
        <nav class="nav-links" [class.open]="menuOpen">
          <a [routerLink]="link('channels')" routerLinkActive="active" (click)="closeMenu()">{{ 'nav.channels' | translate }}</a>
          <a [routerLink]="link('features')" routerLinkActive="active" (click)="closeMenu()">{{ 'nav.features' | translate }}</a>
          <a [routerLink]="link('howItWorks')" routerLinkActive="active" (click)="closeMenu()">{{ 'nav.howItWorks' | translate }}</a>
          <a [routerLink]="link('pricing')" routerLinkActive="active" (click)="closeMenu()">{{ 'nav.pricing' | translate }}</a>
          <a [routerLink]="link('useCases')" routerLinkActive="active" (click)="closeMenu()">{{ 'nav.useCases' | translate }}</a>
          <a [routerLink]="link('blog')" routerLinkActive="active" (click)="closeMenu()">{{ 'nav.blog' | translate }}</a>
          <div class="lang-select nav-lang-mobile">
            <i data-lucide="globe"></i>
            <select [attr.aria-label]="'nav.language' | translate" (change)="onLangChange($event)">
              <option value="it" [selected]="currentLang === 'it'">Italiano</option>
              <option value="en" [selected]="currentLang === 'en'">English</option>
            </select>
          </div>
        </nav>
        <div class="nav-cta">
          <div class="lang-select nav-lang-desktop">
            <i data-lucide="globe"></i>
            <select [attr.aria-label]="'nav.language' | translate" (change)="onLangChange($event)">
              <option value="it" [selected]="currentLang === 'it'">IT</option>
              <option value="en" [selected]="currentLang === 'en'">EN</option>
            </select>
          </div>
          <a class="login" [href]="consoleUrl" target="_blank" rel="noopener">{{ 'nav.login' | translate }}</a>
          <!-- Resta visibile anche a menu chiuso su telefono: il breakpoint nasconde
               .nav-links ma non .nav-cta .btn (vedi styles/_responsive.scss). -->
          <a class="btn btn-primary btn-sm" [routerLink]="link(features.trialPage)" [appTrackClick]="'Lead'">{{ 'nav.trial' | translate }}</a>
          <button class="hamburger" [attr.aria-label]="'nav.menu' | translate" (click)="toggleMenu()">
            <i data-lucide="menu"></i>
          </button>
        </div>
      </div>
    </header>
  `,
})
export class NavbarComponent implements OnInit, OnDestroy {
  menuOpen = false;
  /** The console of this tier (build-time): staging links the staging console, production the live one. */
  protected readonly consoleUrl = environment.consoleUrl;
  private sub?: Subscription;
  private readonly isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly lang: LanguageService,
    private readonly seo: SeoService,
    protected readonly features: FeaturesService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  get currentLang(): string {
    return this.lang.current;
  }

  /**
   * RouterLink target for a page in the current language, using its CANONICAL slug
   * (`/en/features`, not `/en/funzioni`). Both slugs resolve to the same component,
   * but linking the canonical one keeps internal links and hreflang in agreement.
   */
  protected link(page: PageKey): string[] {
    return routerCommands(page, toLang(this.lang.current));
  }

  ngOnInit(): void {
    this.syncLangFromUrl();
    this.sub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.syncLangFromUrl();
        this.menuOpen = false;
      });
  }

  private syncLangFromUrl(): void {
    let r: ActivatedRoute | null = this.route.root;
    let lang: string | undefined;
    while (r) {
      const p = r.snapshot.paramMap.get('lang');
      if (p) lang = p;
      r = r.firstChild;
    }
    // The `:lang` subtree only matches a language we actually serve (`isSupportedLang`
    // in app.routes.ts), so a made-up one never reaches here — it renders the 404 page.
    // What still lands here is the language-less catch-all, which defaults to Italian.
    if (!lang) {
      // Before the first navigation the route tree is still empty: LanguageService has
      // already taken the language from the address, don't overwrite it with Italian.
      if (!this.router.navigated) {
        return;
      }
      lang = 'it';
    }
    if (lang !== this.lang.current) {
      this.lang.setLang(lang);
    } else if (this.isBrowser) {
      document.documentElement.lang = lang;
    }
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  closeMenu(): void {
    this.menuOpen = false;
  }

  /**
   * Switch site language. Jumps to the CANONICAL URL of the same page in the target
   * language (`/it/funzioni` → `/en/features`), which is exactly what the page's
   * `hreflang` alternate advertises — swapping only the language segment would land
   * on the non-canonical twin (`/en/funzioni`) and make the two disagree.
   * Falls back to the plain segment swap for anything without a page mapping.
   */
  onLangChange(e: Event): void {
    const lang = (e.target as HTMLSelectElement).value;
    if (!this.lang.supportedLangs.includes(lang) || lang === this.lang.current) {
      return;
    }
    this.menuOpen = false;
    const page = this.seo.activePage();
    this.lang.setLang(lang);
    if (page) {
      this.router.navigate(routerCommands(page.key, toLang(lang), page.params));
      return;
    }
    const parts = this.router.url.split('/');
    if (parts.length > 1) {
      parts[1] = lang;
    } else {
      parts.push(lang);
    }
    this.router.navigateByUrl(parts.join('/') || '/' + lang);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
